import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BulkUnmatch,
  BulkUnmatchProgressSubscription,
  CancelBulkUnmatchJob,
  GetBulkUnmatchJob,
  PreviewBulkUnmatch,
  RetryBulkUnmatchJob,
} from "src/graphql/conciliacion";
import { importAlertState } from "src/redux/actions/alert";
import { setUpdateDocuments } from "src/redux/actions/documentostributarios";
import { importUpdateTransactions } from "src/redux/actions/conciliacion";
import { updateConcilieantionRatio } from "src/redux/actions/dashboard";
import { bulkUnmatchTargetNoun } from "../utils/bulkUnmatchLabels";

/** Estados desde los que el job ya no avanza. */
const TERMINAL_STATUSES = ["COMPLETED", "PARTIAL", "FAILED", "CANCELLED"];

/**
 * Cada cuánto se consulta el job mientras está en vuelo.
 *
 * NO es redundante con la subscription: el PubSub del server es in-memory por
 * pod, así que el evento solo llega si este cliente quedó conectado al mismo pod
 * que está corriendo el job. Mismo motivo por el que /descargas hace polling cada 5s.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Mismo tope que BULK_UNMATCH_MAX_TARGETS en el server. Se valida acá además de
 * allá porque el ValidationPipe global de Nest no hace llegar los mensajes de
 * class-validator al cliente (quedan como "Bad Request Exception"), y este es el
 * único límite que un usuario puede tocar de verdad con un "seleccionar todo".
 */
export const BULK_UNMATCH_MAX_TARGETS = 20000;

export const isTerminalBulkUnmatchStatus = (status) =>
  TERMINAL_STATUSES.includes(String(status || ""));

/** El porcentaje nunca retrocede: una barra que baja se lee como un error. */
const monotonicPercent = (next, previous) => {
  const value = Number.isFinite(Number(next)) ? Number(next) : 0;
  return Math.max(Number(previous) || 0, value);
};

/**
 * Orquesta la desconciliación masiva desde el front: preflight, lanzamiento del
 * job, seguimiento del avance y refresco de las listas al terminar.
 *
 * @param {object}   params
 * @param {string}   params.companyId
 * @param {() => void} [params.onSettled] Callback al cerrar el job (limpiar selección, refetch).
 */
export const useBulkUnmatch = ({ companyId, onSettled }) => {
  const dispatch = useDispatch();

  const { executeMutation: previewMutation } = PreviewBulkUnmatch();
  const { executeMutation: bulkUnmatchMutation } = BulkUnmatch();
  const { executeMutation: cancelMutation } = CancelBulkUnmatchJob();
  const { executeMutation: retryMutation } = RetryBulkUnmatchJob();

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [job, setJob] = useState(null);
  const [percent, setPercent] = useState(0);

  const jobId = job?.id || null;
  const inFlight = Boolean(jobId) && !isTerminalBulkUnmatchStatus(job?.status);

  // Refs para no re-crear el intervalo ni el handler de la subscription en cada render.
  const settledRef = useRef(false);
  const settledJobIdsRef = useRef(new Set());
  const pendingGroupsRef = useRef([]);
  const chainedTotalsRef = useRef({ unmatched: 0, failed: 0 });
  const launchOneRef = useRef(async () => null);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const { data: polledData, reexecuteQuery: refetchJob } = GetBulkUnmatchJob(jobId, !inFlight);

  /** Refresca las listas y avisa el resultado. Se ejecuta UNA vez por job. */
  const settle = useCallback(
    (finalJob) => {
      // El evento en vivo trae `jobId`, no `id`. Sin esto el cierre se ignora:
      // no parte el grupo siguiente (boletas) y la lista no se refresca.
      const jobIdKey = String(finalJob?.id || finalJob?.jobId || "");
      if (!jobIdKey || settledJobIdsRef.current.has(jobIdKey)) return;
      settledJobIdsRef.current.add(jobIdKey);

      if (pendingGroupsRef.current.length > 0) {
        chainedTotalsRef.current.unmatched += Number(finalJob?.totals?.unmatched || 0);
        chainedTotalsRef.current.failed += Number(finalJob?.totals?.failed || 0);
        const next = pendingGroupsRef.current.shift();
        setPercent(0);
        launchOneRef.current(next).then((created) => {
          if (created) return;
          pendingGroupsRef.current = [];
          dispatch(setUpdateDocuments({ value: true }));
          dispatch(importUpdateTransactions(true));
          dispatch(updateConcilieantionRatio());
          if (onSettledRef.current) onSettledRef.current(finalJob);
        });
        return;
      }

      if (settledRef.current) return;
      settledRef.current = true;

      const totals = finalJob?.totals || {};
      const failed = chainedTotalsRef.current.failed + Number(totals.failed || 0);
      const unmatched = chainedTotalsRef.current.unmatched + Number(totals.unmatched || 0);
      const state =
        finalJob?.status === "FAILED"
          ? "error"
          : finalJob?.status === "PARTIAL" || finalJob?.status === "CANCELLED"
            ? "warning"
            : "success";

      dispatch(
        importAlertState({
          concept:
            finalJob?.status === "PARTIAL"
              ? "Desconciliación parcial"
              : finalJob?.status === "CANCELLED"
                ? "Desconciliación cancelada"
                : undefined,
          content:
            finalJob?.message ||
            `Se deshicieron ${unmatched} match(es)${failed > 0 ? `; ${failed} con error.` : "."}`,
          state,
          duration: failed > 0 ? 8 : 4,
        }),
      );

      dispatch(setUpdateDocuments({ value: true }));
      dispatch(importUpdateTransactions(true));
      dispatch(updateConcilieantionRatio());
      if (onSettledRef.current) onSettledRef.current(finalJob);
    },
    [dispatch],
  );

  const applyJob = useCallback(
    (next) => {
      if (!next) return;
      let merged = null;
      setJob((current) => {
        merged = {
          ...(current || {}),
          ...next,
          id: next.id || current?.id || next.jobId,
        };
        return merged;
      });
      setPercent((current) => monotonicPercent(next.percentage, current));
      if (merged && isTerminalBulkUnmatchStatus(merged.status)) settle(merged);
    },
    [settle],
  );

  // Avance en vivo. Puede no llegar (otro pod); el polling de abajo cubre eso.
  BulkUnmatchProgressSubscription(
    companyId,
    (_prev, response) => {
      const event = response?.bulkUnmatchProgress;
      // Ignora eventos de otros jobs de la misma empresa (canal por companyId).
      if (event && (!jobId || String(event.jobId) === String(jobId))) applyJob(event);
      return response;
    },
    !inFlight,
  );

  // Polling de respaldo mientras el job está en vuelo.
  useEffect(() => {
    if (!inFlight || !jobId) return undefined;
    const timer = setInterval(() => {
      refetchJob({ requestPolicy: "network-only" });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [inFlight, jobId, refetchJob]);

  // Aplica lo que trae el polling. Es la mitad que hace útil al respaldo: sin
  // esto el job se quedaría "en vuelo" para siempre cuando el evento del WS no
  // llega (el PubSub del server es in-memory por pod).
  const polledJob = polledData?.bulkUnmatchJob;
  useEffect(() => {
    if (!polledJob || String(polledJob.id) !== String(jobId)) return;
    applyJob(polledJob);
  }, [polledJob, jobId, applyJob]);

  /** Corre el dry-run. Devuelve el preview para que el modal decida qué mostrar. */
  const runPreview = useCallback(
    async ({ type, ids, deleteMatchCreatedObligations = true }) => {
      const tooMany = tooManyTargetsPreview(ids, type);
      if (tooMany) {
        setPreview(tooMany);
        return tooMany;
      }
      setPreviewing(true);
      setPreview(null);
      try {
        const { data, error } = await previewMutation({
          input: { companyId: String(companyId), type, ids, deleteMatchCreatedObligations },
        });
        if (error) {
          const result = {
            canProceed: false,
            blockers: [extractGraphQLMessage(error)],
            warnings: [],
            targets: ids.length,
            totalMatches: 0,
            entriesToDelete: 0,
            obligationsToDelete: 0,
            blockedByPeriod: 0,
          };
          setPreview(result);
          return result;
        }
        const result = data?.previewBulkUnmatch || null;
        setPreview(result);
        return result;
      } finally {
        setPreviewing(false);
      }
    },
    [companyId, previewMutation],
  );

  /** Suma el preflight de cada tipo. El endpoint no acepta tipos mezclados en un solo job. */
  const runPreviewGroups = useCallback(
    async (groups = []) => {
      setPreviewing(true);
      setPreview(null);
      const merged = {
        canProceed: true,
        blockers: [],
        warnings: [],
        targets: 0,
        targetsWithMatches: 0,
        totalMatches: 0,
        entriesToDelete: 0,
        obligationsToDelete: 0,
        blockedByPeriod: 0,
      };
      try {
        for (const group of groups) {
          const tooMany = tooManyTargetsPreview(group.ids, group.type);
          if (tooMany) {
            setPreview(tooMany);
            return tooMany;
          }
          const { data, error } = await previewMutation({
            input: {
              companyId: String(companyId),
              type: group.type,
              ids: group.ids,
              deleteMatchCreatedObligations: group.deleteMatchCreatedObligations !== false,
            },
          });
          if (error) {
            merged.canProceed = false;
            merged.blockers.push(extractGraphQLMessage(error));
            continue;
          }
          const result = data?.previewBulkUnmatch;
          if (!result) {
            merged.canProceed = false;
            continue;
          }
          merged.targets += Number(result.targets || 0);
          merged.targetsWithMatches += Number(result.targetsWithMatches || 0);
          merged.totalMatches += Number(result.totalMatches || 0);
          merged.entriesToDelete += Number(result.entriesToDelete || 0);
          merged.obligationsToDelete += Number(result.obligationsToDelete || 0);
          merged.blockedByPeriod += Number(result.blockedByPeriod || 0);
          merged.blockers.push(...(result.blockers || []));
          merged.warnings.push(...(result.warnings || []));
          if (!result.canProceed) merged.canProceed = false;
        }
        if (merged.blockers.length > 0 || merged.totalMatches === 0) merged.canProceed = false;
        setPreview(merged);
        return merged;
      } finally {
        setPreviewing(false);
      }
    },
    [companyId, previewMutation],
  );

  /** Un job. No revisa `inFlight`: el encadenado lo llama al terminar el anterior. */
  const launchOne = useCallback(
    async ({ type, ids, deleteMatchCreatedObligations = true }) => {
      if (tooManyTargetsPreview(ids, type)) {
        dispatch(
          importAlertState({
            content: TOO_MANY_TARGETS_MESSAGE(ids.length, type),
            state: "error",
            duration: 8,
          }),
        );
        return null;
      }
      setLaunching(true);
      settledRef.current = false;
      setPercent(0);
      try {
        const { data, error } = await bulkUnmatchMutation({
          input: { companyId: String(companyId), type, ids, deleteMatchCreatedObligations },
        });
        if (error) {
          dispatch(
            importAlertState({
              content: extractGraphQLMessage(error),
              state: "error",
              duration: 8,
            }),
          );
          return null;
        }
        const created = data?.bulkUnmatch || null;
        if (created) {
          setJob(created);
          dispatch(
            importAlertState({
              content: `Deshaciendo matches de ${ids.length} ${bulkUnmatchTargetNoun(
                type,
                ids.length,
              )}…`,
              state: "loading",
              duration: 4,
            }),
          );
        }
        return created;
      } finally {
        setLaunching(false);
      }
    },
    [bulkUnmatchMutation, companyId, dispatch],
  );

  launchOneRef.current = launchOne;

  /**
   * Lanza el job. Si vienen varios grupos (boleta + retención), el siguiente
   * parte cuando el anterior termina: el endpoint acepta un solo `type`.
   */
  const launch = useCallback(
    async (selection) => {
      const groups =
        Array.isArray(selection?.groups) && selection.groups.length > 0
          ? selection.groups
          : [
              {
                type: selection?.type,
                ids: selection?.ids,
                deleteMatchCreatedObligations: selection?.deleteMatchCreatedObligations,
              },
            ];
      if (launching || inFlight) return null;
      chainedTotalsRef.current = { unmatched: 0, failed: 0 };
      pendingGroupsRef.current = groups.slice(1);
      return launchOne(groups[0]);
    },
    [inFlight, launchOne, launching],
  );

  const cancel = useCallback(async () => {
    if (!jobId) return;
    const { error } = await cancelMutation({ jobId });
    if (error) {
      dispatch(
        importAlertState({ content: extractGraphQLMessage(error), state: "error", duration: 6 }),
      );
    }
  }, [cancelMutation, dispatch, jobId]);

  /** Reintenta solo lo fallido/pendiente del job actual. */
  const retry = useCallback(async () => {
    if (!jobId) return null;
    const { data, error } = await retryMutation({ jobId });
    if (error) {
      dispatch(
        importAlertState({ content: extractGraphQLMessage(error), state: "error", duration: 6 }),
      );
      return null;
    }
    const created = data?.retryBulkUnmatchJob || null;
    if (created) {
      settledRef.current = false;
      setPercent(0);
      setJob(created);
    }
    return created;
  }, [dispatch, jobId, retryMutation]);

  const reset = useCallback(() => {
    setJob(null);
    setPreview(null);
    setPercent(0);
    settledRef.current = false;
    pendingGroupsRef.current = [];
    chainedTotalsRef.current = { unmatched: 0, failed: 0 };
  }, []);

  return {
    preview,
    previewing,
    runPreview,
    runPreviewGroups,
    launch,
    launching,
    job,
    percent,
    inFlight,
    cancel,
    retry,
    reset,
  };
};

const TOO_MANY_TARGETS_MESSAGE = (count, type) =>
  `Seleccionaste ${count.toLocaleString("es-CL")} ${bulkUnmatchTargetNoun(type, count)} y el máximo por operación es ` +
  `${BULK_UNMATCH_MAX_TARGETS.toLocaleString("es-CL")}. Filtra la lista y hazlo por partes.`;

/** Preview sintético para el tope: evita un round-trip que el server rechazaría igual. */
function tooManyTargetsPreview(ids = [], type) {
  if (ids.length <= BULK_UNMATCH_MAX_TARGETS) return null;
  return {
    canProceed: false,
    blockers: [TOO_MANY_TARGETS_MESSAGE(ids.length, type)],
    warnings: [],
    targets: ids.length,
    targetsWithMatches: 0,
    totalMatches: 0,
    entriesToDelete: 0,
    obligationsToDelete: 0,
    blockedByPeriod: 0,
  };
}

/** Urql envuelve el mensaje del server en graphQLErrors y le pega un "[GraphQL]". */
export function extractGraphQLMessage(error, fallback = "No se pudo deshacer los matches.") {
  const raw =
    error?.graphQLErrors?.[0]?.message ||
    error?.networkError?.result?.errors?.[0]?.message ||
    error?.message ||
    fallback;
  return String(raw).replace(/^\[GraphQL\]\s*/i, "").trim() || fallback;
}
